// ══════════════════════════════════════════════════════════════════════════════
//  FleetMaint.js — Fleet › Maintenance tab                      #FLEET_MAINT_V1 (2026-10-05)
//
//  Bill: "We are using GEO TAB for Codes, Maintenance Workorders and DVIRs. Could we integrate this
//  into our own system and import codes, develop workorders, build out scheduled maintenance and
//  flag units that are Overdue or hard to catch? Some units stay out for long periods of time so we
//  could need a way to flag when unit is here at Ennis yard."
//
//  STEP 1 of that build (work orders are the next step and will sit on top of this screen):
//    • SERVICE DUE      every scheduled unit, judged on its LAST KNOWN reading by the one shared rule
//                       in fleetService.js — the same answer Weekly Inventory shows.
//    • AT ENNIS         a unit that is due AND physically here is pulled to the top in bold: it may
//                       not be back for months. "Here" = Geotab GPS inside the yard radius when the
//                       unit has a tracker reporting, else today's Daily Inventory (A-SHOP / A-IDLE).
//    • ✓ SERVICED       one form: reading, date, what was done, who did it. Writes Serviced + DOS
//                       (so Weekly Inventory's Service Due moves on its own) and a line in the unit's
//                       service history.
//    • FAULT CODES / DVIR DEFECTS / DARK TRACKERS   read-only lists from api/geotab-maint-sync.js.
//
//  The schedule is DOPE's, not Geotab's. Geotab's own "PM services due" report was checked against
//  the fleet on 10/5/26: its odometers agree with DOPE (25 of 26 trucks within 500 mi) but its engine
//  hours on VACs / excavators do not (23 of 25 off by more than 15%), so its overdue list is not
//  imported. Geotab feeds this screen only what it measures well: position, fault codes, DVIRs.
//
//  STORAGE READ HERE
//    fleet-summary        state[unitKey] = { bucket:'A-SHOP'|'A-IDLE'|'A-OH'|'JOB', since }  (Daily roll)
//    fleet-daily-<mon>    today's assigned name + DUC per unit
//    fleet-shop-notes     thirdParty flag — "in the shop but not our shop"
//    fleet-geotab-maint   { generatedAt, yard, positions[], faults[], dvirs[], dvirCounts, errors }
//    fleet-service-log    { v, entries[] } — service history (written by FleetModule.writeServiceUnits)
// ══════════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useMemo, Fragment } from "react";
import { vinClean } from "./unitValid";
import { svcNormDos, svcTodayMDY, svcParseDate, svcSummary, svcFmtDate, svcAddMonths, SVC_MECHANICS, NICK_SWAP } from "./fleetService";   // #NICK_SWAP_V1 — label only
// #SVC_WORK_ITEMS_V1 (2026-10-22) — Bill: "Drop down for Service for standard PM, Brakes, Inspection, Vital Fluids, Lights,
// Tires (able to check each one and populate into list). But leave a small area to freely type."
const SVC_WORK_ITEMS = ['Standard PM', 'Brakes', 'Inspection', 'Vital Fluids', 'Lights', 'Tires'];

const pad2 = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const mondayOf = (d) => { const x = new Date(d); const b = (x.getDay() + 6) % 7; x.setDate(x.getDate() - b); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const num = (n) => (n == null || isNaN(Number(n))) ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 });
// A timestamp → "10/05/26 7:42a" (local). Blank when there is nothing to show.
const fmtWhen = (v) => {
  if (!v) return '';
  const d = new Date(v); if (isNaN(d)) return '';
  let h = d.getHours(); const ap = h >= 12 ? 'p' : 'a'; h = h % 12 || 12;
  return `${svcFmtDate(d)} ${h}:${pad2(d.getMinutes())}${ap}`;
};
const hoursSince = (v) => { const d = new Date(v); return isNaN(d) ? null : (Date.now() - d.getTime()) / 3600000; };
const GPS_FRESH_HRS = 72;    // a position older than this is "last seen", not "is here"
const DARK_DAYS = 3;         // a tracker silent this long is listed under Dark trackers
// #FLEET_MAINT_FAULTS_V2 (2026-10-05, after the first live pull: 622 lines, 447 of them from the tracker itself).
// Geotab files its own tracker's housekeeping (firmware, resets, unplugged) in the same feed as the truck's
// codes. That noise is hidden unless asked for. Everything else the tracker reports — warning light on, TPMS,
// low battery, coolant level — is about the vehicle and stays in front of the mechanic.
const TRACKER_NOISE = /telematics device|firmware|accelerometer|collision|device reset|\bIOX\b|camera|harsh event/i;
export const isTrackerMsg = (f) => !!(f && f.tracker) && TRACKER_NOISE.test(String(f.desc || ''));
// J1939 SPNs from 520192 up are the engine maker's own codes — Geotab has no name for them.
const faultText = (d) => String(d || '').replace(/^\*+\s*Unknown Diagnostic\s+(\d+)/i, 'Manufacturer-specific code $1 (no name in Geotab)');
const lampOf = (f) => f.red ? 'RED STOP' : f.amber ? 'AMBER' : f.mil ? 'CHECK ENGINE' : f.protect ? 'PROTECT' : '';
const lampRank = (f) => f.red ? 3 : f.amber ? 2 : (f.mil || f.protect) ? 1 : 0;
const HERE_CARDS = 12;       // cards in the AT ENNIS banner before it folds into "+ N more"; the table holds the full list

// Bob's note: "severity (low, medium, high or critical)". Derived, never typed:
//   Critical  a full interval (or 180 days) past due      High    past due
//   Medium    coming due (last 10% / 14 days)             Low     on schedule
export const svcSeverity = (x) => {
  if (!x || !x.state) return null;
  if (x.state === 'overdue') {
    const overFrac = (x.left != null && x.rule && x.rule.interval) ? (-x.left / x.rule.interval) : 0;
    const daysOver = x.daysLeft != null ? -x.daysLeft : 0;
    return (overFrac >= 1 || daysOver >= 180) ? 'Critical' : 'High';
  }
  if (x.state === 'soon') return 'Medium';
  if (x.state === 'ok') return 'Low';
  return null;
};

export default function FleetMaint({ F, sg, board, canEdit, onServiced, onOpenWeekly, focusUnit, onFocusUsed }) {
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState({});          // unitKey -> { duc, assigned, day }
  const [shopNotes, setShopNotes] = useState({});
  const [geo, setGeo] = useState(null);            // fleet-geotab-maint, or {} when the pull has never run
  const [log, setLog] = useState([]);
  const [loadedAt, setLoadedAt] = useState('');
  const [filter, setFilter] = useState('due');     // due | here | overdue | soon | unknown | all
  const [bucket, setBucket] = useState('');
  const [q, setQ] = useState('');
  const [openUk, setOpenUk] = useState(null);      // the row with the ✓ Serviced form open
  const [form, setForm] = useState({});
  const [workOpen, setWorkOpen] = useState(false);   // #SVC_WORK_ITEMS_V1 — the What-was-done checklist dropdown
  const [busy, setBusy] = useState('');
  const [histUk, setHistUk] = useState(null);
  const [showTracker, setShowTracker] = useState(false);
  const [section, setSection] = useState('service');   // service | faults | dvir | dark
  const [faultQ, setFaultQ] = useState('');
  const [faultOpen, setFaultOpen] = useState({});     // unit key -> expanded
  const [faultAll, setFaultAll] = useState(false);
  const [sort, setSort] = useState({});               // #FLEET_MAINT_SORT_V1 — section -> { k, dir } ; nothing = the built-in order
  // #FLEET_RIG_FILE_V1 (2026-10-09) — opened from a rig file on the Fleet dash: land on Service Due with that one unit
  // showing whatever its state (All + the unit in the search box), ready for ✓ Serviced. Taken once, then handed back.
  useEffect(() => {
    if (!focusUnit) return;
    setSection('service'); setFilter('all'); setBucket(''); setQ(String(focusUnit));
    if (onFocusUsed) onFocusUsed();
  }, [focusUnit]);   // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    const mon = mondayOf(new Date());
    const safe = async (k) => { try { return await sg(k); } catch { return null; } };
    const [s, w, wPrev, n, g, l] = await Promise.all([
      safe('fleet-summary'), safe('fleet-daily-' + iso(mon)), safe('fleet-daily-' + iso(addDays(mon, -7))),
      safe('fleet-shop-notes'), safe('fleet-geotab-maint'), safe('fleet-service-log'),
    ]);
    setSummary(s && typeof s === 'object' ? s : {});
    // Latest Daily line per unit: walk this week newest → oldest, then last week, first hit wins.
    const d = {};
    const walk = (wk) => {
      if (!wk || typeof wk !== 'object') return;
      Object.keys(wk).filter(k => /^\d{4}-\d{2}-\d{2}$/.test(k)).sort().reverse().forEach(day => {
        const rec = wk[day] || {};
        Object.keys(rec).forEach(uk => {
          if (d[uk]) return;
          const c = rec[uk] || {};
          const duc = String(c.duc || '').trim().toUpperCase(), assigned = String(c.assigned || '').trim();
          if (duc || assigned) d[uk] = { duc, assigned, day };
        });
      });
    };
    walk(w); walk(wPrev);
    setDaily(d);
    setShopNotes((n && n.notes && typeof n.notes === 'object') ? n.notes : {});
    setGeo(g && typeof g === 'object' ? g : {});
    setLog(l && Array.isArray(l.entries) ? l.entries : []);
    setLoadedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
  };
  useEffect(() => { load(); }, [sg]);   // eslint-disable-line react-hooks/exhaustive-deps

  const posByVin = useMemo(() => {
    const m = {};
    ((geo && geo.positions) || []).forEach(p => { const v = vinClean(p.vin); if (v) m[v] = p; });
    return m;
  }, [geo]);
  const unitByVin = useMemo(() => {
    const m = {};
    (board || []).forEach(x => { const v = vinClean(x.vin); if (v) m[v] = x; });
    return m;
  }, [board]);

  // Where is it right now? GPS when a tracker has reported in the last 72 hours, else Daily Inventory.
  const whereOf = (uk, vin) => {
    const p = posByVin[vinClean(vin)];
    const age = p && p.at ? hoursSince(p.at) : null;
    const gpsOk = p && p.lat != null && p.lng != null;
    if (gpsOk && age != null && age <= GPS_FRESH_HRS) {
      return { src: 'gps', here: !!p.atYard, label: p.atYard ? 'AT ENNIS' : `${num(Math.round(p.distMi))} mi out`, at: p.at };
    }
    const st = (summary && summary.state && summary.state[uk]) || null;
    const dl = daily[uk] || null;
    const code = (dl && dl.duc) || (st && st.bucket) || '';
    const third = !!(shopNotes[uk] && shopNotes[uk].thirdParty);
    if (code === 'A-SHOP') return { src: 'daily', here: !third, label: third ? '3RD-PARTY SHOP' : 'IN SHOP', at: dl && dl.day, who: (shopNotes[uk] && shopNotes[uk].mechanic) || '' };
    // A-IDLE on Daily is the best "it is sitting in the yard" signal for a unit with no tracker (rigs).
    if (code === 'A-IDLE') return { src: 'daily', here: true, label: 'IDLE · YARD', at: dl && dl.day, who: dl && dl.assigned };
    if (code === 'A-OH') return { src: 'daily', here: false, label: 'OVERHEAD', at: dl && dl.day, who: dl && dl.assigned };
    if (dl && (dl.duc || dl.assigned)) return { src: 'daily', here: false, label: dl.duc ? `JOB ${dl.duc}` : 'ASSIGNED', at: dl.day, who: dl.assigned };
    if (gpsOk) return { src: 'gps-old', here: false, label: p.atYard ? 'last seen at Ennis' : `last seen ${num(Math.round(p.distMi))} mi out`, at: p.at };
    return { src: null, here: false, label: 'not on Daily', at: null };
  };

  const rows = useMemo(() => (board || []).map(x => {
    const where = whereOf(x.uk, x.vin);
    return { ...x, where, sev: svcSeverity(x) };
  }), [board, posByVin, summary, daily, shopNotes]);   // eslint-disable-line react-hooks/exhaustive-deps

  const due = rows.filter(x => x.state === 'overdue' || x.state === 'soon');
  const hereDue = due.filter(x => x.where.here);
  const counts = {
    overdue: rows.filter(x => x.state === 'overdue').length,
    soon: rows.filter(x => x.state === 'soon').length,
    here: hereDue.length,
    unknown: rows.filter(x => x.state === 'unknown').length,
    all: rows.length,
  };
  // Worst first: how far past due as a share of the interval (or days past the date rule).
  const sevScore = (x) => {
    const r = (x.left != null && x.rule && x.rule.interval && x.gap !== 'reading-below-serviced') ? (-x.left / x.rule.interval) : -9;
    const d = x.daysLeft != null ? (-x.daysLeft / 180) : -9;
    return Math.max(r, d);
  };
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter(x => {
      if (bucket && x.bucket !== bucket) return false;
      if (filter === 'due' && !(x.state === 'overdue' || x.state === 'soon')) return false;
      if (filter === 'here' && !((x.state === 'overdue' || x.state === 'soon') && x.where.here)) return false;
      if (filter === 'overdue' && x.state !== 'overdue') return false;
      if (filter === 'soon' && x.state !== 'soon') return false;
      if (filter === 'unknown' && x.state !== 'unknown') return false;
      if (!s) return true;
      return [x.unit, x.desc, x.vin, x.where.label, x.where.who, x.bucketLabel].some(v => String(v || '').toLowerCase().includes(s));
    }).sort((a, b) => (Number(b.where.here && b.state !== 'ok') - Number(a.where.here && a.state !== 'ok'))
      || (sevScore(b) - sevScore(a)) || String(a.unit).localeCompare(String(b.unit)));
  }, [rows, filter, bucket, q]);   // eslint-disable-line react-hooks/exhaustive-deps
  const buckets = useMemo(() => {
    const seen = []; rows.forEach(x => { if (!seen.find(b => b[0] === x.bucket)) seen.push([x.bucket, x.bucketLabel]); });
    return seen;
  }, [rows]);

  // ── Geotab lists ───────────────────────────────────────────────────────────
  const geoRan = !!(geo && geo.generatedAt);
  const geoErr = (geo && geo.errors) || {};
  const nameOf = (rec) => { const u = unitByVin[vinClean(rec.vin)]; return u ? u.unit : String(rec.name || '').trim(); };
  const descOf = (rec) => { const u = unitByVin[vinClean(rec.vin)]; return u ? u.desc : ''; };
  const faultsAll = (geo && geo.faults) || [];
  const vehicleFaults = faultsAll.filter(f => !isTrackerMsg(f));
  const faults = showTracker ? faultsAll : vehicleFaults;
  // One row per unit, worst lamp first; the codes sit underneath it.
  const faultUnits = (() => {
    const m = {};
    faults.forEach(f => {
      const u = unitByVin[vinClean(f.vin)];
      const key = u ? u.uk : ('dev:' + (f.deviceId || f.name));
      const g = m[key] || (m[key] = { key, u, unit: u ? u.unit : String(f.name || '').trim(), desc: u ? u.desc : '', lines: [], rank: 0, lastAt: '' });
      g.lines.push(f);
      g.rank = Math.max(g.rank, lampRank(f));
      if (String(f.lastAt || '') > g.lastAt) g.lastAt = String(f.lastAt || '');
    });
    const s = faultQ.trim().toLowerCase();
    return Object.values(m)
      .filter(g => !s || [g.unit, g.desc, ...g.lines.map(l => `${l.code} ${l.desc} ${l.failure}`)].some(v => String(v || '').toLowerCase().includes(s)))
      .sort((a, b) => (b.rank - a.rank) || b.lastAt.localeCompare(a.lastAt));
  })();
  const faultUnitCount = new Set(vehicleFaults.map(f => vinClean(f.vin) || f.deviceId || f.name)).size;
  const dvirs = (geo && geo.dvirs) || [];
  const dvirDefects = [];
  dvirs.forEach(dv => (dv.defects || []).forEach(df => dvirDefects.push({ dv, df })));
  const openDefects = dvirDefects.filter(x => !/^(repaired|notnecessary|not necessary)$/i.test(String(x.df.repairStatus || '')));
  const dark = ((geo && geo.positions) || []).filter(p => {
    const a = p.at ? hoursSince(p.at) : null;
    return a == null || a > DARK_DAYS * 24 || p.communicating === false;
  }).sort((a, b) => String(a.at || '').localeCompare(String(b.at || '')));

  // ── ✓ Serviced ─────────────────────────────────────────────────────────────
  // #SVC_LAST_FIX_V1 (2026-10-22) — Bill: "Many of these were plugged into GEO TAB and didn't transfer… need to update
  // the Hours/Miles it was serviced." Click Last service or DOS on a row → same form, but pre-filled with what is on
  // file (not today / not the current reading) so a service already done (Geotab, paper, 3rd-party) can be keyed in.
  const openForm = (x, mode) => {
    setOpenUk(x.uk);
    if (mode === 'fix') {
      setWorkOpen(false);
      setForm({ mode: 'fix', reading: x.serviced != null ? String(x.serviced) : '', dos: x.dos ? svcNormDos(x.dos) : '',
        items: ['Standard PM'], extra: 'back-filled from Geotab', mechanic: '', shop: '' });
      return;
    }
    setWorkOpen(false);
    setForm({ reading: x.reading != null ? String(x.reading) : '', dos: svcTodayMDY(),
      items: ['Standard PM'], extra: '', mechanic: '', shop: '' });
  };
  const saveForm = async (x) => {
    const reading = String(form.reading || '').replace(/,/g, '').trim();
    if (reading === '' || isNaN(Number(reading))) { alert('Type the reading on the unit at the time of service.'); return; }
    if (!svcParseDate(form.dos)) { alert('Date of service must be MM/DD/YY.'); return; }
    if (x.serviced != null && Number(reading) < Number(x.serviced)
      && !window.confirm(`${x.unit}: ${Number(reading).toLocaleString()} is LOWER than the last service on file (${Number(x.serviced).toLocaleString()}). Save it anyway?`)) return;
    if (x.reading != null && Number(reading) > Number(x.reading)
      && !window.confirm(`${x.unit}: ${Number(reading).toLocaleString()} is HIGHER than the unit's current reading (${Number(x.reading).toLocaleString()}). Save it anyway?`)) return;
    const third = form.mechanic === '3rd-party shop';
    const who = (form.mechanic === 'Other' || third) ? String(form.shop || '').trim() : form.mechanic;
    setBusy('saving…');
    try {
      await onServiced(x, { serviced: Number(reading), dos: svcNormDos(form.dos), work: [...SVC_WORK_ITEMS.filter(i => (form.items || []).includes(i)), String(form.extra || '').trim()].filter(Boolean).join(', '),
        mechanic: third ? '' : who, shop: third ? (who || '3rd-party shop') : '' });
      setOpenUk(null); setForm({});
      try { const l = await sg('fleet-service-log'); setLog(l && Array.isArray(l.entries) ? l.entries : []); } catch {}
    } catch (e) { alert('Could not save: ' + (e.message || e)); }
    setBusy('');
  };

  // ── styles (same steel palette as the rest of Fleet — F is handed in) ─────────
  const btn = { background: F.cardHi, border: `1px solid ${F.line}`, color: F.text, borderRadius: 8, padding: '6px 10px', fontSize: 11.5, fontWeight: 800, cursor: 'pointer' };
  const inp = { background: F.bg, border: `1px solid ${F.line}`, borderRadius: 7, padding: '6px 9px', fontSize: 12.5, color: F.text, outline: 'none', boxSizing: 'border-box' };
  // #FLEET_MAINT_SORT_V1 (2026-10-06) — Bill: "Dark Trackers and other tables should be aligned centered and sortable."
  // All four Maintenance tables: every column centered, every header clickable. First click sorts A→Z / low→high,
  // second click reverses, third click goes back to the built-in order (worst first). Blanks always sink to the bottom.
  const th = { padding: '8px 10px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: .4, color: F.muted, textAlign: 'center', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: F.cardHi, zIndex: 1 };
  const td = { padding: '6px 10px', fontSize: 12.5, color: F.text, verticalAlign: 'middle', textAlign: 'center' };
  const tdl = { ...td, textAlign: 'left' };   // the code lines under a fault-code unit stay left: they are sentences, not columns
  const nOr = (v) => (v == null || v === '' || isNaN(Number(v))) ? null : Number(v);
  const tOr = (v) => { if (!v) return null; const t = new Date(v).getTime(); return isNaN(t) ? null : t; };
  const sortRows = (sec, list, getters) => {
    const c = sort[sec]; const get = c && getters[c.k];
    if (!get) return list;
    const blank = (v) => v == null || v === '' || (typeof v === 'number' && isNaN(v));
    return list.slice().sort((a, b) => {
      const va = get(a), vb = get(b); const ba = blank(va), bb = blank(vb);
      if (ba || bb) return ba === bb ? 0 : (ba ? 1 : -1);
      const d = (typeof va === 'number' && typeof vb === 'number') ? va - vb : String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      return c.dir === 'desc' ? -d : d;
    });
  };
  const clickSort = (sec, k) => setSort(o => { const c = o[sec]; return { ...o, [sec]: (!c || c.k !== k) ? { k, dir: 'asc' } : c.dir === 'asc' ? { k, dir: 'desc' } : null }; });
  const sortTh = (sec, k, label) => {
    const c = sort[sec]; const on = !!(c && k && c.k === k);
    return (
      <th key={k || label || 'x'} onClick={k ? () => clickSort(sec, k) : undefined}
        title={k ? 'Click to sort · again to reverse · a third time for the normal order' : undefined}
        style={{ ...th, cursor: k ? 'pointer' : 'default', userSelect: 'none', color: on ? F.text : F.muted }}>
        {label}{on ? (c.dir === 'asc' ? ' ▲' : ' ▼') : ''}
      </th>
    );
  };
  const SEV_RANK = { Critical: 4, High: 3, Medium: 2, Low: 1 };
  const svcSorted = sortRows('service', shown, {
    unit: x => x.unit, desc: x => x.desc, type: x => x.bucketLabel, where: x => x.where.label, who: x => x.where.who,
    sev: x => SEV_RANK[x.sev] || null, last: x => nOr(x.serviced), dos: x => { const d = svcParseDate(x.dos); return d ? d.getTime() : null; },
    now: x => nOr(x.reading), due: x => nOr(x.due), status: x => { const v = sevScore(x); return v <= -9 ? null : v; },
  });
  const faultSorted = sortRows('faults', faultUnits, {
    unit: g => g.unit, desc: g => g.desc, where: g => g.u ? whereOf(g.u.uk, g.u.vin).label : null,
    lamp: g => g.rank || null, codes: g => g.lines.length, last: g => tOr(g.lastAt),
  });
  const dvirSorted = sortRows('dvir', dvirDefects, {
    date: r => tOr(r.dv.at), unit: r => nameOf(r.dv), desc: r => descOf(r.dv), driver: r => r.dv.driver, type: r => r.dv.type,
    defect: r => r.df.name, remark: r => r.df.remark || r.dv.remark, safe: r => r.dv.safe === false ? 0 : r.dv.safe === true ? 1 : null,
    repair: r => /^(repaired|notnecessary|not necessary)$/i.test(String(r.df.repairStatus || '')) ? String(r.df.repairStatus) : 'OPEN',
  });
  const darkUnit = (p) => unitByVin[vinClean(p.vin)] || null;
  const darkDaily = (p) => { const u = darkUnit(p); const dl = u && daily[u.uk]; const st = u && summary && summary.state && summary.state[u.uk]; return dl ? `${dl.duc || ''} ${dl.assigned || ''}`.trim() : (st ? st.bucket : ''); };
  const darkSorted = sortRows('dark', dark, {
    unit: p => { const u = darkUnit(p); return u ? u.unit : null; }, desc: p => { const u = darkUnit(p); return u ? u.desc : null; },
    name: p => String(p.name || '').trim(), heard: p => tOr(p.at), days: p => { const a = p.at ? hoursSince(p.at) : null; return a == null ? null : Math.floor(a / 24); },
    pos: p => p.lat == null ? null : (p.atYard ? 0 : nOr(Math.round(p.distMi))), daily: p => darkDaily(p),
  });
  const stateColor = (s) => s === 'overdue' ? F.red : s === 'soon' ? F.amber : s === 'ok' ? F.green : F.muted;
  const sevColor = { Critical: F.red, High: F.orange, Medium: F.amber, Low: F.green };
  const chip = (on) => ({ ...btn, padding: '5px 11px', background: on ? F.accent : F.cardHi, color: on ? '#04121C' : F.text });
  const tab = (on) => ({ ...btn, padding: '7px 14px', fontSize: 12.5, background: on ? F.text : F.cardHi, color: on ? '#101317' : F.text });
  const whereTag = (w) => (
    <span title={w.at ? `${w.src === 'gps' || w.src === 'gps-old' ? 'Geotab GPS' : 'Daily Inventory'} · ${/^\d{4}-\d{2}-\d{2}$/.test(String(w.at)) ? svcNormDos(w.at) : fmtWhen(w.at)}` : undefined}
      style={{ display: 'inline-block', fontSize: 10.5, fontWeight: 900, letterSpacing: .4, padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap',
        background: w.here ? F.green : 'transparent', color: w.here ? '#04121C' : (w.src ? F.text : F.muted), border: `1px solid ${w.here ? F.green : F.line}` }}>
      {w.label}{w.src === 'gps' ? ' · GPS' : ''}{w.src === 'daily' && w.at && w.at !== todayISO ? ` · ${svcNormDos(w.at).slice(0, 5)}` : ''}
    </span>
  );
  const todayISO = iso(new Date());
  const history = histUk ? log.filter(e => e.uk === histUk).sort((a, b) => String(b.at).localeCompare(String(a.at))) : [];

  return (
    <Fragment>
      {/* section switch + counts */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <button onClick={() => setSection('service')} style={tab(section === 'service')}>🔧 Service Due · {counts.overdue + counts.soon}</button>
        <button onClick={() => setSection('faults')} style={tab(section === 'faults')}>⚠ Fault Codes{geoRan ? ` · ${faultUnitCount}` : ''}</button>
        <button onClick={() => setSection('dvir')} style={tab(section === 'dvir')}>📋 DVIR Defects{geoRan ? ` · ${openDefects.length}` : ''}</button>
        <button onClick={() => setSection('dark')} style={tab(section === 'dark')}>📡 Dark Trackers{geoRan ? ` · ${dark.length}` : ''}</button>
        <div style={{ flex: 1 }} />
        {busy && <span style={{ fontSize: 11.5, color: F.gold, fontWeight: 700 }}>{busy}</span>}
        <span style={{ fontSize: 11, color: F.muted }}>
          {geoRan ? `Geotab pulled ${fmtWhen(geo.generatedAt)}` : 'Geotab maintenance pull has not run yet'}{loadedAt ? ` · screen loaded ${loadedAt}` : ''}
        </span>
        <button onClick={load} style={btn}>⟳ Refresh</button>
      </div>

      {section === 'service' && (
        <Fragment>
          {/* AT ENNIS — SERVICE NOW. Bob: "Alert location at Ennis when service is needed! Bold attention
              as unit may not return for a long time or delay its departure." */}
          {hereDue.length > 0 && (
            <div style={{ background: '#2A1512', border: `2px solid ${F.red}`, borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 900, color: F.red, letterSpacing: .6, marginBottom: 8 }}>
                AT ENNIS — SERVICE BEFORE IT LEAVES · {hereDue.length} UNIT{hereDue.length === 1 ? '' : 'S'}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {hereDue.slice().sort((a, b) => (Number(b.where.src === 'gps') - Number(a.where.src === 'gps')) || (sevScore(b) - sevScore(a))).slice(0, HERE_CARDS).map(x => (
                  <button key={x.uk} onClick={() => { setFilter('here'); setBucket(''); setQ(x.unit); }}
                    title={`${x.desc} — ${svcSummary(x, x.rule)}`}
                    style={{ ...btn, textAlign: 'left', background: F.card, borderColor: stateColor(x.state), borderLeft: `4px solid ${stateColor(x.state)}`, padding: '6px 10px' }}>
                    <div style={{ fontSize: 13.5, fontWeight: 900 }}>{x.unit} <span style={{ fontWeight: 700, color: F.muted, fontSize: 11.5 }}>{x.desc}</span></div>
                    <div style={{ fontSize: 11, color: stateColor(x.state), fontWeight: 800 }}>{svcSummary(x, x.rule)} · {x.where.label}</div>
                  </button>
                ))}
                {hereDue.length > HERE_CARDS && (
                  <button onClick={() => { setFilter('here'); setBucket(''); setQ(''); }} style={{ ...btn, color: F.red, borderColor: F.red, padding: '6px 14px' }}>
                    + {hereDue.length - HERE_CARDS} more — show all
                  </button>
                )}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {[['due', `Due · ${counts.overdue + counts.soon}`], ['here', `At Ennis · ${counts.here}`], ['overdue', `Overdue · ${counts.overdue}`],
              ['soon', `Due soon · ${counts.soon}`], ['unknown', `Needs data · ${counts.unknown}`], ['all', `All scheduled · ${counts.all}`]].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} style={chip(filter === k)}>{l}</button>
            ))}
            <select value={bucket} onChange={e => setBucket(e.target.value)} style={{ ...inp, padding: '5px 8px', fontSize: 11.5 }}>
              <option value="">All types</option>
              {buckets.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search unit, description, job, name…" style={{ ...inp, width: 230, padding: '5px 9px', fontSize: 11.5 }} />
            {q && <button onClick={() => setQ('')} style={{ ...btn, color: F.muted }}>✕</button>}
          </div>
          <div style={{ fontSize: 11, color: F.muted, marginBottom: 10 }}>
            Judged on each unit's <b>last known</b> reading, not only this week's. Rigs, Cleaners, VACs, Excavators, Mixing Systems and Forklifts every 250 hrs · 18-Wheelers every 15,000 mi or 6 months, whichever comes first · Trucks every 6,000 mi.
            Due soon = last 10% of the interval or 14 days. <b>✓ Serviced</b> updates Serviced and DOS in Weekly Inventory and adds a line to the unit's service history.
          </div>

          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 330px)', border: `1px solid ${F.line}`, borderRadius: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                {[['unit', 'Unit'], ['desc', 'Description'], ['type', 'Type'], ['where', 'Where'], ['who', 'With'], ['sev', 'Severity'],
                  ['last', 'Last service'], ['dos', 'DOS'], ['now', 'Now'], ['due', 'Due at'], ['status', 'Status'], [null, '']].map(([k, l]) => sortTh('service', k, l))}
              </tr></thead>
              <tbody>
                {svcSorted.map(x => {
                  const u = x.rule.metric === 'mi' ? 'mi' : 'hrs';
                  const open = openUk === x.uk;
                  return (
                    <Fragment key={x.uk}>
                      <tr style={{ borderTop: `1px solid ${F.line}`, background: x.where.here && (x.state === 'overdue' || x.state === 'soon') ? '#221614' : 'transparent' }}>
                        <td style={{ ...td, fontWeight: 900, whiteSpace: 'nowrap' }}>{x.unit}</td>
                        <td style={td}>{x.desc}</td>
                        <td style={{ ...td, fontSize: 11, color: F.muted, whiteSpace: 'nowrap' }}>{x.bucketLabel}</td>
                        <td style={td}>{whereTag(x.where)}</td>
                        <td style={{ ...td, fontSize: 11.5, color: F.muted }}>{x.where.who || '—'}</td>
                        <td style={{ ...td, fontSize: 11, fontWeight: 900, color: sevColor[x.sev] || F.muted, whiteSpace: 'nowrap' }}>{x.sev || '—'}</td>
                        <td onClick={canEdit ? () => (open && form.mode === 'fix') ? setOpenUk(null) : openForm(x, 'fix') : undefined}
                          title={canEdit ? 'Click to correct the last service reading / date (e.g. serviced in Geotab)' : undefined}
                          style={{ ...td, whiteSpace: 'nowrap', cursor: canEdit ? 'pointer' : 'default' }}>
                          <span style={canEdit ? { borderBottom: `1px dashed ${F.muted}` } : undefined}>{x.serviced != null ? num(x.serviced) : '—'}</span>{canEdit ? <span style={{ fontSize: 10, color: F.muted, marginLeft: 4 }}>✎</span> : null}
                        </td>
                        <td onClick={canEdit ? () => (open && form.mode === 'fix') ? setOpenUk(null) : openForm(x, 'fix') : undefined}
                          title={canEdit ? 'Click to correct the last service reading / date' : undefined}
                          style={{ ...td, whiteSpace: 'nowrap', fontSize: 11.5, cursor: canEdit ? 'pointer' : 'default' }}>{x.dos ? svcNormDos(x.dos) : '—'}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}
                          title={x.readingAt ? `Reading for the week of ${svcNormDos(x.readingAt)} · ${x.readingSource === 'geotab' ? 'Geotab' : (x.readingSource === 'oem' ? 'factory feed' : 'typed in')}` : 'No reading on file'}>
                          {x.reading != null ? <>{num(x.reading)} <span style={{ fontSize: 10, color: F.muted }}>{u}</span></> : '—'}
                          {x.readingAt && <div style={{ fontSize: 9.5, color: F.muted }}>wk {svcNormDos(x.readingAt).slice(0, 5)}{(x.readingSource === 'geotab' || x.readingSource === 'oem') ? '' : ' · M'}</div>}
                        </td>
                        <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 800 }}>
                          {x.due != null ? num(x.due) : '—'}
                          {x.dueDate && <div style={{ fontSize: 9.5, color: F.muted, fontWeight: 700 }}>or {svcFmtDate(x.dueDate)}</div>}
                        </td>
                        <td style={{ ...td, fontSize: 11.5, fontWeight: 800, color: stateColor(x.state) }}>
                          {x.state === 'overdue' ? '⚠ ' : ''}{svcSummary(x, x.rule) || '—'}
                        </td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          <button onClick={() => setHistUk(histUk === x.uk ? null : x.uk)} title="Service history for this unit"
                            style={{ ...btn, padding: '4px 8px', fontSize: 11, color: F.muted, marginRight: 5 }}>History{log.some(e => e.uk === x.uk) ? ` · ${log.filter(e => e.uk === x.uk).length}` : ''}</button>
                          {canEdit && <button onClick={() => open ? setOpenUk(null) : openForm(x)}
                            style={{ ...btn, padding: '4px 10px', fontSize: 11.5, background: open ? F.cardHi : F.green, color: open ? F.text : '#04121C', borderColor: F.green }}>{open ? 'Cancel' : '✓ Serviced'}</button>}
                        </td>
                      </tr>
                      {open && (
                        <tr style={{ background: F.card }}>
                          <td colSpan={12} style={{ padding: '10px 12px' }}>
                            {form.mode === 'fix' && (
                              <div style={{ fontSize: 11.5, fontWeight: 800, color: F.gold, marginBottom: 8 }}>
                                ✎ CORRECT LAST SERVICE — type the {u} and date {x.unit} was actually serviced (e.g. logged in Geotab). Now reads {x.reading != null ? `${num(x.reading)} ${u}` : '—'}.
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                              <label style={{ fontSize: 10.5, color: F.muted, fontWeight: 800 }}>READING AT SERVICE ({u.toUpperCase()})<br />
                                <input value={form.reading || ''} inputMode="decimal" onChange={e => setForm(f => ({ ...f, reading: e.target.value }))} style={{ ...inp, width: 130, textAlign: 'right' }} /></label>
                              <label style={{ fontSize: 10.5, color: F.muted, fontWeight: 800 }}>DATE OF SERVICE<br />
                                <input value={form.dos || ''} placeholder="MM/DD/YY" onChange={e => setForm(f => ({ ...f, dos: e.target.value }))} style={{ ...inp, width: 110, textAlign: 'center' }} /></label>
                              <div style={{ fontSize: 10.5, color: F.muted, fontWeight: 800, position: 'relative' }}>WHAT WAS DONE<br />
                                <button type="button" onClick={() => setWorkOpen(o => !o)}
                                  style={{ ...inp, width: 230, textAlign: 'left', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {(form.items || []).length ? SVC_WORK_ITEMS.filter(i => form.items.includes(i)).join(', ') : 'Pick service items…'} ▾
                                </button>
                                {workOpen && (
                                  <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 5, marginTop: 3, background: F.cardHi, border: `1px solid ${F.line}`, borderRadius: 8, padding: '6px 10px', minWidth: 200, boxShadow: '0 6px 18px rgba(0,0,0,.45)' }}>
                                    {SVC_WORK_ITEMS.map(i => {
                                      const on = (form.items || []).includes(i);
                                      return (
                                        <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 12.5, color: F.text, fontWeight: 700, cursor: 'pointer' }}>
                                          <input type="checkbox" checked={on}
                                            onChange={() => setForm(f => { const cur = f.items || []; return { ...f, items: on ? cur.filter(y => y !== i) : [...cur, i] }; })} />
                                          {i}
                                        </label>
                                      );
                                    })}
                                    <button type="button" onClick={() => setWorkOpen(false)} style={{ ...btn, marginTop: 4, padding: '3px 10px', fontSize: 11 }}>Done</button>
                                  </div>
                                )}
                              </div>
                              <label style={{ fontSize: 10.5, color: F.muted, fontWeight: 800, flex: '1 1 160px' }}>NOTES<br />
                                <input value={form.extra || ''} onChange={e => setForm(f => ({ ...f, extra: e.target.value }))} placeholder="15W-40, air filter…" style={{ ...inp, width: '100%' }} /></label>
                              <label style={{ fontSize: 10.5, color: F.muted, fontWeight: 800 }}>DONE BY<br />
                                <select value={form.mechanic || ''} onChange={e => setForm(f => ({ ...f, mechanic: e.target.value }))} style={{ ...inp, width: 150 }}>
                                  <option value="">—</option>
                                  {SVC_MECHANICS.map(m => <option key={m} value={m}>{NICK_SWAP[m] || m}</option>)}
                                  <option value="3rd-party shop">3rd-party shop</option>
                                  <option value="Other">Other</option>
                                </select></label>
                              {(form.mechanic === '3rd-party shop' || form.mechanic === 'Other') && (
                                <label style={{ fontSize: 10.5, color: F.muted, fontWeight: 800 }}>{form.mechanic === 'Other' ? 'NAME' : 'SHOP NAME'}<br />
                                  <input value={form.shop || ''} onChange={e => setForm(f => ({ ...f, shop: e.target.value }))} style={{ ...inp, width: 180 }} /></label>
                              )}
                              <button onClick={() => saveForm(x)} disabled={!!busy} style={{ ...btn, padding: '7px 16px', background: F.green, color: '#04121C', borderColor: F.green }}>{form.mode === 'fix' ? 'Save last service' : 'Save service'}</button>
                            </div>
                            <div style={{ fontSize: 10.5, color: F.muted, marginTop: 6 }}>
                              Next due will read {form.reading && !isNaN(Number(String(form.reading).replace(/,/g, ''))) ? num(Number(String(form.reading).replace(/,/g, '')) + x.rule.interval) : '—'} {u}
                              {x.rule.months && svcParseDate(form.dos) ? ` or ${svcFmtDate(svcAddMonths(svcParseDate(form.dos), x.rule.months))}` : ''}.
                            </div>
                          </td>
                        </tr>
                      )}
                      {histUk === x.uk && (
                        <tr style={{ background: F.card }}>
                          <td colSpan={12} style={{ padding: '8px 12px' }}>
                            {history.length === 0 ? (
                              <div style={{ fontSize: 11.5, color: F.muted }}>No service history logged in DOPE yet for {x.unit}. History starts with the first ✓ Serviced saved here or the first Serviced / DOS change in Weekly Inventory.</div>
                            ) : (
                              <table style={{ borderCollapse: 'collapse', fontSize: 11.5 }}>
                                <tbody>
                                  {history.map(e => (
                                    <tr key={e.id}>
                                      <td style={{ padding: '2px 12px 2px 0', fontWeight: 800 }}>{e.dos || '—'}</td>
                                      <td style={{ padding: '2px 12px 2px 0', textAlign: 'right' }}>{e.serviced != null ? num(e.serviced) : '—'} {u}</td>
                                      <td style={{ padding: '2px 12px 2px 0' }}>{e.work || '—'}</td>
                                      <td style={{ padding: '2px 12px 2px 0', color: F.muted }}>{e.shop ? `${e.shop} (3rd party)` : (e.mechanic || '—')}</td>
                                      <td style={{ padding: '2px 0', color: F.muted, fontSize: 10.5 }}>entered {fmtWhen(e.at)}{e.by ? ` · ${String(e.by).split('@')[0]}` : ''}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {!shown.length && (
                  <tr><td colSpan={12} style={{ ...td, textAlign: 'center', color: F.muted, padding: 24 }}>
                    {filter === 'here' ? 'Nothing due is sitting at Ennis right now.' : 'Nothing matches this filter.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          {onOpenWeekly && <div style={{ marginTop: 8 }}><button onClick={onOpenWeekly} style={{ ...btn, color: F.muted }}>Open Weekly Inventory →</button></div>}
        </Fragment>
      )}

      {section !== 'service' && !geoRan && (
        <div style={{ background: F.card, border: `1px solid ${F.amber}55`, borderRadius: 10, padding: 14, fontSize: 12.5 }}>
          <div style={{ fontWeight: 900, color: F.amber, marginBottom: 4 }}>Waiting on the first Geotab maintenance pull</div>
          <div style={{ color: F.muted }}>This list fills from <b>api/geotab-maint-sync</b> (GPS position, fault codes, DVIRs). It runs on its own every two hours on work days once it is deployed; nothing has been written yet.</div>
        </div>
      )}

      {section === 'faults' && geoRan && (
        <Fragment>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11.5, color: F.muted }}>
              Codes Geotab logged in the last {geo.faultDays || 14} days — <b style={{ color: F.text }}>{faultUnitCount} units, {vehicleFaults.length} codes</b>. Worst lamp first. Click a unit for its codes.
            </span>
            <input value={faultQ} onChange={e => setFaultQ(e.target.value)} placeholder="Search unit, code, fault…" style={{ ...inp, width: 210, padding: '5px 9px', fontSize: 11.5 }} />
            <button onClick={() => { setFaultAll(v => !v); setFaultOpen({}); }} style={{ ...btn, color: F.muted }}>{faultAll ? 'Collapse all' : 'Expand all'}</button>
            <label style={{ fontSize: 11.5, color: F.muted, cursor: 'pointer' }}>
              <input type="checkbox" checked={showTracker} onChange={e => setShowTracker(e.target.checked)} style={{ marginRight: 5 }} />
              show tracker messages too ({faultsAll.length - vehicleFaults.length})
            </label>
            {geoErr.faults && <span style={{ fontSize: 11.5, color: F.red, fontWeight: 800 }}>Fault pull failed: {String(geoErr.faults).slice(0, 140)}</span>}
          </div>
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 270px)', border: `1px solid ${F.line}`, borderRadius: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{[[null, ''], ['unit', 'Unit'], ['desc', 'Description'], ['where', 'Where'], ['lamp', 'Worst lamp'], ['codes', 'Codes'], ['last', 'Last seen']].map(([k, l]) => sortTh('faults', k, l))}</tr></thead>
              <tbody>
                {faultSorted.map(g => {
                  const open = faultAll ? faultOpen[g.key] !== false : !!faultOpen[g.key];
                  const worst = g.rank === 3 ? 'RED STOP' : g.rank === 2 ? 'AMBER' : g.rank === 1 ? 'CHECK ENGINE' : '';
                  const clr = g.rank === 3 ? F.red : g.rank === 2 ? F.amber : F.muted;
                  return (
                    <Fragment key={g.key}>
                      <tr onClick={() => setFaultOpen(o => ({ ...o, [g.key]: !open }))} style={{ borderTop: `1px solid ${F.line}`, cursor: 'pointer' }}>
                        <td style={{ ...td, width: 18, color: F.muted }}>{open ? '▾' : '▸'}</td>
                        <td style={{ ...td, fontWeight: 900, whiteSpace: 'nowrap' }}>{g.unit}{!g.u && <div style={{ fontSize: 9.5, fontWeight: 800, color: F.amber }}>no VIN match on Master</div>}</td>
                        <td style={td}>{g.desc}</td>
                        <td style={td}>{g.u ? whereTag(whereOf(g.u.uk, g.u.vin)) : '—'}</td>
                        <td style={{ ...td, fontSize: 11, fontWeight: 900, color: clr, whiteSpace: 'nowrap' }}>{worst || '—'}</td>
                        <td style={{ ...td, fontWeight: 800 }}>{g.lines.length}</td>
                        <td style={{ ...td, fontSize: 11.5, whiteSpace: 'nowrap' }}>{fmtWhen(g.lastAt)}</td>
                      </tr>
                      {open && (
                        <tr style={{ background: F.card }}>
                          <td />
                          <td colSpan={6} style={{ padding: '4px 10px 10px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <tbody>
                                {g.lines.slice().sort((x, y) => (lampRank(y) - lampRank(x)) || String(y.lastAt || '').localeCompare(String(x.lastAt || ''))).map((f, i) => (
                                  <tr key={i} style={{ borderTop: i ? `1px solid ${F.line}` : 'none' }}>
                                    <td style={{ ...tdl, fontSize: 11.5, whiteSpace: 'nowrap', width: 130, fontWeight: 800 }}>{f.code || '—'}</td>
                                    <td style={{ ...tdl, fontSize: 12 }}>{faultText(f.desc) || '—'}{f.failure ? <span style={{ color: F.muted }}> — {f.failure}</span> : null}
                                      <span style={{ fontSize: 10, color: F.muted }}> · {isTrackerMsg(f) ? 'tracker' : (f.source || '')}</span></td>
                                    <td style={{ ...tdl, fontSize: 10.5, fontWeight: 900, whiteSpace: 'nowrap', width: 100, color: f.red ? F.red : f.amber ? F.amber : F.muted }}>{lampOf(f) || '—'}</td>
                                    <td style={{ ...tdl, fontSize: 11.5, textAlign: 'right', whiteSpace: 'nowrap', width: 70 }} title="Times Geotab logged it in the window">{num(f.count)}×</td>
                                    <td style={{ ...tdl, fontSize: 11, whiteSpace: 'nowrap', width: 250, color: F.muted }}>{fmtWhen(f.firstAt)} → {fmtWhen(f.lastAt)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {!faultUnits.length && <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: F.muted, padding: 24 }}>{faultQ ? 'Nothing matches that search.' : 'No fault codes in the window.'}</td></tr>}
              </tbody>
            </table>
          </div>
        </Fragment>
      )}

      {section === 'dvir' && geoRan && (
        <Fragment>
          <div style={{ fontSize: 11.5, color: F.muted, marginBottom: 8 }}>
            Defects drivers wrote up in Geotab Drive in the last {geo.dvirDays || 14} days
            {geo.dvirCounts ? ` — ${num(geo.dvirCounts.total)} inspections turned in, ${num(geo.dvirCounts.withDefects)} with a defect` : ''}. Only this rolling window is held in DOPE.
            {geoErr.dvirs && <span style={{ color: F.red, fontWeight: 800 }}> DVIR pull failed: {String(geoErr.dvirs).slice(0, 140)}</span>}
          </div>
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 270px)', border: `1px solid ${F.line}`, borderRadius: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{[['date', 'Date'], ['unit', 'Unit'], ['desc', 'Description'], ['driver', 'Driver'], ['type', 'Inspection'], ['defect', 'Defect'], ['remark', 'Remark'], ['safe', 'Safe to run'], ['repair', 'Repair']].map(([k, l]) => sortTh('dvir', k, l))}</tr></thead>
              <tbody>
                {dvirSorted.map(({ dv, df }, i) => {
                  const done = /^(repaired|notnecessary|not necessary)$/i.test(String(df.repairStatus || ''));
                  return (
                    <tr key={i} style={{ borderTop: `1px solid ${F.line}`, opacity: done ? .6 : 1 }}>
                      <td style={{ ...td, fontSize: 11.5, whiteSpace: 'nowrap' }}>{fmtWhen(dv.at)}</td>
                      <td style={{ ...td, fontWeight: 900, whiteSpace: 'nowrap' }}>{nameOf(dv)}</td>
                      <td style={td}>{descOf(dv)}</td>
                      <td style={td}>{dv.driver || '—'}</td>
                      <td style={{ ...td, fontSize: 11.5 }}>{dv.type || '—'}</td>
                      <td style={{ ...td, fontWeight: 800 }}>{df.part ? <span style={{ color: F.muted, fontWeight: 700 }}>{df.part} › </span> : null}{df.name || '—'}{/critical/i.test(String(df.severity || '')) ? <span style={{ color: F.red }}> · CRITICAL</span> : null}</td>
                      <td style={{ ...td, fontSize: 11.5 }}>{df.remark || dv.remark || '—'}</td>
                      <td style={{ ...td, fontSize: 11.5, fontWeight: 800, color: dv.safe === false ? F.red : F.muted }}>{dv.safe === false ? 'NO' : dv.safe === true ? 'Yes' : '—'}</td>
                      <td style={{ ...td, fontSize: 11.5, color: done ? F.green : F.amber, fontWeight: 800 }}>{done ? `${df.repairStatus}${df.repairedAt ? ' ' + fmtWhen(df.repairedAt) : ''}` : 'OPEN'}</td>
                    </tr>
                  );
                })}
                {!dvirDefects.length && <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: F.muted, padding: 24 }}>No defects written up in the window.</td></tr>}
              </tbody>
            </table>
          </div>
        </Fragment>
      )}

      {section === 'dark' && geoRan && (
        <Fragment>
          <div style={{ fontSize: 11.5, color: F.muted, marginBottom: 8 }}>
            Trackers that have not reported a position in {DARK_DAYS}+ days, or that Geotab marks as not communicating — unplugged, dead battery, or a unit parked out of coverage. Oldest first. Click any column heading to sort.
            {geoErr.positions && <span style={{ color: F.red, fontWeight: 800 }}> Position pull failed: {String(geoErr.positions).slice(0, 140)}</span>}
          </div>
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 270px)', border: `1px solid ${F.line}`, borderRadius: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{[['unit', 'Unit'], ['desc', 'Description'], ['name', 'Geotab name'], ['heard', 'Last heard'], ['days', 'Days dark'], ['pos', 'Last position'], ['daily', 'Daily says']].map(([k, l]) => sortTh('dark', k, l))}</tr></thead>
              <tbody>
                {darkSorted.map((p, i) => {
                  const u = unitByVin[vinClean(p.vin)];
                  const a = p.at ? hoursSince(p.at) : null;
                  const st = u && summary && summary.state && summary.state[u.uk];
                  const dl = u && daily[u.uk];
                  return (
                    <tr key={i} style={{ borderTop: `1px solid ${F.line}` }}>
                      <td style={{ ...td, fontWeight: 900, whiteSpace: 'nowrap' }}>{u ? u.unit : <span style={{ color: F.amber }}>no VIN match</span>}</td>
                      <td style={td}>{u ? u.desc : ''}</td>
                      <td style={{ ...td, fontSize: 11.5, color: F.muted }}>{String(p.name || '').trim()}</td>
                      <td style={{ ...td, fontSize: 11.5, whiteSpace: 'nowrap' }}>{p.at ? fmtWhen(p.at) : 'never'}</td>
                      <td style={{ ...td, fontWeight: 900, color: a == null || a > 14 * 24 ? F.red : F.amber }}>{a == null ? '—' : Math.floor(a / 24)}</td>
                      <td style={{ ...td, fontSize: 11.5 }}>{p.lat == null ? '—' : (p.atYard ? 'Ennis yard' : `${num(Math.round(p.distMi))} mi from Ennis`)}</td>
                      <td style={{ ...td, fontSize: 11.5, color: F.muted }}>{dl ? `${dl.duc || ''} ${dl.assigned || ''}`.trim() : (st ? st.bucket : '—')}</td>
                    </tr>
                  );
                })}
                {!dark.length && <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: F.muted, padding: 24 }}>Every tracker has reported in the last {DARK_DAYS} days.</td></tr>}
              </tbody>
            </table>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
}
